import { BILLING_CYCLES, labelOf, useExpenses, type Expense } from "@/api/finance";
import { ExpenseDocs } from "@/components/expenses/ExpenseDocs";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { renewalBadge, statusBadge } from "@/lib/bills";
import { formatDate, formatInr, formatMoney } from "@/lib/format";

type BillsPanelProps = {
  /** Opens the expense form with a billing cycle preset. */
  onRecord?: () => void;
  onMarkPaid?: (expense: Expense) => void;
  /** Opens the bill and proof, to attach them or change their links. */
  onEditLinks?: (expense: Expense) => void;
};

function period(bill: Expense): string {
  if (!bill.period_from && !bill.period_to) return "—";
  return `${formatDate(bill.period_from)} – ${formatDate(bill.period_to)}`;
}

function amount(bill: Expense): string {
  return bill.currency === "INR"
    ? formatInr(bill.amount)
    : `${formatMoney(bill.amount, bill.currency)} = ${formatInr(bill.inr_amount)}`;
}

/** The tracker's IT Bills sheet: every recurring bill, soonest renewal first. */
export function BillsPanel({ onRecord, onMarkPaid, onEditLinks }: BillsPanelProps) {
  const bills = useExpenses({ recurring: true });

  if (bills.isPending) return <p className="text-muted-foreground">Loading bills…</p>;
  if (bills.isError) return <RequestError error={bills.error} />;
  if (bills.data.length === 0) {
    return (
      <EmptyState
        title="No recurring bills yet"
        description="Software, cloud, domain and other subscriptions go here with their billing cycle, so the next renewal is flagged two weeks ahead."
        action={
          onRecord && (
            <Button size="sm" onClick={onRecord}>
              Record a bill
            </Button>
          )
        }
      />
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-3 md:hidden">
        {bills.data.map((bill) => {
          const renewal = renewalBadge(bill);
          const status = statusBadge(bill);
          return (
            <li key={bill.id} className="rounded-lg border bg-card p-4 text-sm shadow-sm">
              <p className="font-semibold">{bill.description}</p>
              <p>{amount(bill)}</p>
              <p className="text-xs text-muted-foreground">
                {[bill.payee, labelOf(BILLING_CYCLES, bill.billing_cycle), period(bill)]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <p className="mt-1 flex flex-wrap gap-2">
                {bill.next_renewal && <span className="text-xs">Renews {formatDate(bill.next_renewal)}</span>}
                {renewal && <Badge variant={renewal.tone}>{renewal.label}</Badge>}
                {status && <Badge variant={status.tone}>{status.label}</Badge>}
              </p>
              <ExpenseDocs expense={bill} />
            </li>
          );
        })}
      </ul>
      <div className="hidden table-frame md:block">
        <table className="data-table">
          <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">Service</th>
              <th className="px-4 py-2 font-medium">Cycle</th>
              <th className="px-4 py-2 font-medium">Period</th>
              <th className="px-4 py-2 font-medium">Next renewal</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 text-right font-medium">Amount</th>
              <th className="px-4 py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {bills.data.map((bill) => {
              const renewal = renewalBadge(bill);
              const status = statusBadge(bill);
              return (
                <tr key={bill.id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    <span className="font-medium">{bill.description}</span>
                    <span className="block text-xs text-muted-foreground">
                      {[bill.payee, bill.category_name, formatDate(bill.spent_on)]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <ExpenseDocs expense={bill} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    {labelOf(BILLING_CYCLES, bill.billing_cycle)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">{period(bill)}</td>
                  <td className="whitespace-nowrap px-4 py-2">
                    {bill.next_renewal ? formatDate(bill.next_renewal) : "—"}
                    {renewal && (
                      <Badge variant={renewal.tone} className="ml-2">
                        {renewal.label}
                      </Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    {status ? <Badge variant={status.tone}>{status.label}</Badge> : "Paid"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {amount(bill)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {status && onMarkPaid && (
                      <Button size="sm" variant="outline" onClick={() => onMarkPaid(bill)}>
                        {bill.status === "unpaid" ? "Mark paid" : "Mark reimbursed"}
                      </Button>
                    )}
                    {onEditLinks && (
                      <Button size="sm" variant="ghost" onClick={() => onEditLinks(bill)}>
                        Bill & proof
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
